import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, BarChart3, Car, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import SEOHead from '../../components/SEOHead';
import { PORTAL_LINKS } from '../../constants/site';

const calculators = [
  {
    title: 'Loan Repayment Calculator',
    description: 'Estimate repayments for personal and business lending with flexible terms and rates.',
    path: '/equipment-finance-calculator',
    icon: Calculator,
  },
  {
    title: 'Borrowing Power Calculator',
    description: 'Estimate how much you may be able to borrow based on income, expenses, and debt.',
    path: '/truck-finance-calculator',
    icon: BarChart3,
  },
  {
    title: 'Asset Finance Repayment Calculator',
    description: 'Model repayments for cars, equipment, and commercial assets including balloon options.',
    path: '/lease-vs-buy-calculator',
    icon: Car,
  },
  {
    title: 'Commercial Cashflow Impact Calculator',
    description: 'Understand how repayment changes affect monthly and annual business cashflow.',
    path: '/asset-refinance-calculator',
    icon: Building2,
  },
];

export default function CalculatorsHub() {
  return (
    <>
      <SEOHead
        title="Finance Calculators Australia | Flash Funding"
        description="Use Flash Funding's free finance calculators to estimate repayments, borrowing power and cash flow impact. Tools for business and personal finance."
        canonical="/calculators"
      />

      <section className="py-16 sm:py-20" data-testid="calculators-hub-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <p className="mb-3 text-xs uppercase tracking-[0.2em] text-purple-700" data-testid="calculators-hub-eyebrow">Flash Funding Tools</p>
            <h1 className="mb-4 text-4xl font-heading text-[#0F0A1A] sm:text-5xl" data-testid="calculators-hub-heading">Free Finance Calculators for Australian Borrowers</h1>
            <p className="text-base text-[#4A4556]" data-testid="calculators-hub-subheading">Use these tools to plan repayments and borrowing range, then move to the right finance pathway.</p>
            <p className="mt-3 text-sm text-[#6C657E]" data-testid="calculators-hub-route-priority-text">
              Start with the best product path first, then continue in the secure customer portal.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="calculators-hub-route-links">
              <Link to="/car-loans" className="text-sm text-purple-700 hover:text-purple-800" data-testid="calculators-hub-route-car-link">Car Loans</Link>
              <span className="text-[#9A92AF]">•</span>
              <Link to="/personal-loans" className="text-sm text-purple-700 hover:text-purple-800" data-testid="calculators-hub-route-personal-link">Personal Loans</Link>
              <span className="text-[#9A92AF]">•</span>
              <Link to="/commercial-car-loans" className="text-sm text-purple-700 hover:text-purple-800" data-testid="calculators-hub-route-commercial-car-link">Commercial Car Loans</Link>
              <span className="text-[#9A92AF]">•</span>
              <Link to="/business-finance" className="text-sm text-purple-700 hover:text-purple-800" data-testid="calculators-hub-route-business-link">Business Finance</Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="calculators-hub-grid">
            {calculators.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="card-hover rounded-2xl border border-purple-200 bg-white p-6"
                data-testid={`calculator-card-${item.path.slice(1)}`}
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-purple-100 text-purple-700" data-testid={`calculator-card-icon-${item.path.slice(1)}`}>
                  <item.icon size={20} />
                </div>
                <h2 className="mb-2 font-semibold text-[#1B1431]">{item.title}</h2>
                <p className="mb-4 text-sm text-[#4A4556]">{item.description}</p>
                <span className="inline-flex items-center text-sm text-purple-700">Open calculator <ArrowRight size={14} className="ml-1" /></span>
              </Link>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-purple-200 bg-white p-7 text-center" data-testid="calculators-hub-cta-panel">
            <h3 className="mb-3 text-2xl font-heading text-[#1B1431]">Ready to apply?</h3>
            <p className="mb-6 text-[#4A4556]">Choose your finance category first, then complete the guided application.</p>
            <a href={PORTAL_LINKS.GENERIC} data-testid="calculators-hub-apply-link">
              <Button className="rounded-full bg-purple-600 px-7 py-6 text-xs uppercase tracking-[0.14em] text-white hover:bg-purple-700" data-testid="calculators-hub-primary-cta">
                See What You Can Get Approved For
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
